import { useState } from "react";

import type { Accessory, AccessoryOption } from "../types";
import styles from "./AccessoryCard.module.css";

/**
 * The accessories card — its own card under the shortlist, like the budget
 * slider, never part of a laptop's row.
 *
 * Two states, one component:
 *
 *  * categories, when the server sent `accessory_options`. A tap asks for
 *    that one kind. Nothing is shown until something is tapped, so a
 *    customer who only wants a laptop is never sold at.
 *  * products, when the server sent `accessory_match` for the kind they
 *    tapped. Each row links straight to the Dell store.
 *
 * Both lists come from the server, chosen from the live catalog — in stock
 * only, and monitors filtered to what the machine can actually drive. This
 * component renders what it is given and decides nothing.
 */

type Labels = {
  heading: string;
  blurb: string;
  chosen: string;
  showAll: (n: number) => string;
  showLess: string;
};

const LABELS: Record<string, Labels> = {
  en: {
    heading: "WHAT ELSE DO YOU NEED?",
    blurb: "Playing on a desk? These go with it. Tap one to see what's in stock.",
    chosen: "IN STOCK",
    showAll: (n) => `Show all ${n}`,
    showLess: "Show fewer",
  },
  hinglish: {
    heading: "AUR KYA CHAHIYE?",
    blurb: "Desk par khelte ho? Ye saath mein acche lagte hain. Tap karke dekh lijiye.",
    chosen: "STOCK MEIN",
    showAll: (n) => `Saare ${n} dikhaiye`,
    showLess: "Kam dikhaiye",
  },
  hi: {
    heading: "और क्या चाहिए?",
    blurb: "डेस्क पर खेलते हैं? ये साथ में अच्छे लगते हैं। टैप करके देख लीजिए।",
    chosen: "स्टॉक में",
    showAll: (n) => `सभी ${n} दिखाएं`,
    showLess: "कम दिखाएं",
  },
};

function labelsFor(language?: string | null): Labels {
  const code = (language ?? "en").toLowerCase();
  return LABELS[code] ?? LABELS.en;
}

interface Props {
  options?: AccessoryOption[] | null;
  /** Written per use case by the server, so it is also in the right
   *  language. The generic line below is only a fallback. */
  blurb?: string | null;
  items?: Accessory[] | null;
  language?: string | null;
  disabled?: boolean;
  onPick: (text: string) => void;
}

export function AccessoryCard({
  options,
  blurb,
  items,
  language,
  disabled,
  onPick,
}: Props) {
  const t = labelsFor(language);
  // Three at a time. A category with more keeps the rest behind a toggle,
  // so one tap does not drop seven rows into the conversation.
  const [expanded, setExpanded] = useState(false);
  const products = items ?? [];
  const VISIBLE = 3;
  const hasMore = products.length > VISIBLE;
  const shown = expanded ? products : products.slice(0, VISIBLE);
  const categories = options ?? [];
  if (products.length === 0 && categories.length === 0) return null;

  return (
    <div className={styles.card}>
      <div className={styles.head}>
        <span className={styles.tag}>
          {products.length > 0 ? t.chosen : t.heading}
        </span>
      </div>

      {/* Above the rows as well as instead of them. When the server has
          already chosen the set — the mouse, keyboard and monitor asked for
          alongside the laptop — this line is what adds it all up against
          the budget ("poora setup ₹2,95,687, aapke ₹3,00,000 ke andar"),
          and hiding it the moment products appear hid exactly the answer
          the customer asked for. The generic "tap one" fallback stays in the
          chips state only: above a list of products it asks the customer to
          do what they have just done. */}
      {(products.length === 0 || blurb) && (
        <p className={styles.blurb}>{blurb || t.blurb}</p>
      )}

      {products.length > 0 && (
        <div className={styles.rows}>
          {shown.map((a, i) => (
            <a
              key={a.order_code || i}
              className={styles.row}
              href={a.product_url}
              target="_blank"
              rel="noreferrer noopener"
            >
              <span className={styles.rowName}>{a.product_name}</span>
              {/* Price only. The server sends in-stock accessories and
                  nothing else, so the heading above already says IN STOCK
                  and repeating it on every row said the same word twice. */}
              <span className={styles.rowMeta}>
                <span className={styles.rowPrice}>{a.price || "—"}</span>
              </span>
            </a>
          ))}
          {/* Only when there is actually more to see — a category holding
              one product gets no toggle. */}
          {hasMore && (
            <button
              type="button"
              className={styles.more}
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded ? t.showLess : t.showAll(products.length)}
            </button>
          )}
        </div>
      )}

      {/* Kept below the products too, so switching from keyboards to mice
          is one tap rather than a trip back up the transcript. */}
      {categories.length > 0 && (
        <>
          <div className={styles.chips}>
            {categories.map((c) => (
              <button
                key={c.kind}
                type="button"
                className={styles.chip}
                disabled={disabled}
                onClick={() => onPick(`Show me ${c.label}`)}
              >
                {c.label}
              </button>
            ))}
          </div>
        </>
      )}

    </div>
  );
}
